import { PayloadTooLargeError } from '@openphone/internal-api-client'
import { makeAutoObservable, runInAction } from 'mobx'

import type Service from '@src/service'
import Collection from '@src/service/collections/Collection'
import PersistedCollection from '@src/service/collections/PersistedCollection'
import type { Conversation } from '@src/service/conversation-store'
import { ActivityModel } from '@src/service/conversation-store'
import type { PostCallActivitiesRequest } from '@src/service/dto/request/activities/calls'
import type { CodableMessageMedia } from '@src/service/model'
import type { ActivityDecodableReaction } from '@src/service/model/reactions/ActivityReaction'
import ActivityReaction, {
  isActivityReaction,
} from '@src/service/model/reactions/ActivityReaction'
import makePersistable from '@src/service/storage/makePersistable'
import type { SendActivityAddress } from '@src/service/transport/communication'
import type { TransactionConfiguration } from '@src/service/transport/transaction'
import type { ActivityLabelsUpdate } from '@src/service/transport/websocket'
import type { ActivityRepository } from '@src/service/worker/repository/activity'

export default class ActivityStore {
  readonly collection: PersistedCollection<ActivityModel, ActivityRepository>

  /**
   * A collection of voicemail activities, indexed by their SID
   * Used for linking `call` activities to their corresponding `voicemail` activity.
   */
  private readonly voicemailActivitiesBySid = new Collection<ActivityModel>({
    idKey: 'sid',
  })
  private lastFetchedAt: { [key: string]: number } = {}

  /**
   * Holds an stat of how many unicode points in
   * total are stored among all call transcript
   */
  public callTranscriptUnicodePointsCountByActivityId = new Map<string, number>()

  constructor(private service: Service) {
    this.collection = new PersistedCollection({
      table: service.storage.table('activity'),
      classConstructor: () => new ActivityModel(),
    })

    makeAutoObservable(this, {
      callTranscriptUnicodePointsCountByActivityId: false,
    })

    makePersistable<this, 'lastFetchedAt' | 'pageInfos'>(this, 'ActivityStore', {
      lastFetchedAt: service.storage.async(),
      pageInfos: service.storage.async(),
    })

    this.handleWebsocket()

    this.collection.observe((changes) => {
      const voicemails = changes.objects.filter((o) => o.type === 'voicemail')

      const getTranscriptSize = (activity: ActivityModel) =>
        activity.callTranscript?.dialogue.reduce(
          (acc, item) => acc + item.content.length,
          0,
        )

      const storeTranscriptSizeCount = (activities: readonly ActivityModel[]) => {
        for (const activity of activities) {
          const sizeOfTranscript = getTranscriptSize(activity)

          if (!sizeOfTranscript) {
            continue
          }

          this.callTranscriptUnicodePointsCountByActivityId.set(
            activity.id,
            sizeOfTranscript,
          )
        }
      }

      const clearTranscriptSizeCount = (activities: readonly ActivityModel[]) => {
        for (const activity of activities) {
          this.callTranscriptUnicodePointsCountByActivityId.delete(activity.id)
        }
      }

      if (changes.type === 'put') {
        this.voicemailActivitiesBySid.putBulk(voicemails)
        storeTranscriptSizeCount(changes.objects)
      } else if (changes.type === 'delete') {
        this.voicemailActivitiesBySid.deleteBulk(voicemails)
        clearTranscriptSizeCount(changes.objects)
      }
    })
  }

  get(id: string) {
    return this.collection.get(id)
  }

  getVoicemailBySid(sid: string) {
    return this.voicemailActivitiesBySid.get(sid)
  }

  get callTranscriptsUnicodePointsCount(): number {
    return [...this.callTranscriptUnicodePointsCountByActivityId.values()].reduce(
      (acc, count) => acc + count,
      0,
    )
  }

  /**
   * Tries to get an activity from memory first, then the underlying storage,
   * then from the backend.
   */
  getById = async (id: string): Promise<ActivityModel | null> => {
    let activity = this.collection.get(id, { skipStorage: true })

    if (activity) {
      return activity
    }

    activity = (await this.collection.performQuery((repo) => repo.get(id))) ?? null

    if (activity) {
      return activity
    }

    try {
      const response = await this.service.transport.communication.activities.bulk({
        ids: [id],
      })

      const activities = await this.collection.load(response)

      // @ts-expect-error unchecked index access
      return activities[0]
    } catch {
      return null
    }
  }

  sendRaw(
    params: SendActivityAddress & {
      body: string
      media?: CodableMessageMedia[]
    },
  ) {
    return this.service.transport.communication.activities.send(params)
  }

  loadByIds(ids: string[]) {
    ids = ids.filter((id) => !this.collection.isInMemory(id))
    return this.collection.performQuery((repo) => repo.getBulk(ids))
  }

  async send(activity: ActivityModel, conversationFromCaller?: Conversation) {
    const conversationId = activity.conversationId

    if (!conversationId) {
      return Promise.reject(
        new Error('Can not send an activity without a conversation id'),
      )
    }

    this.service.conversation.historyService.addActivityToCache(activity)

    runInAction(() => {
      activity.status = 'queued'
      this.collection.put(activity)
    })

    try {
      const conversation =
        this.service.conversation.collection.get(conversationId) ??
        conversationFromCaller ??
        (await this.service.conversation.getById(conversationId))

      const address = conversation ? addressForConversation(conversation) : null

      if (!conversation || !address) {
        throw new Error(
          "We couldn't find who to send this message to. Please refresh and try again.",
        )
      }

      runInAction(() => {
        conversation.lastActivityId = activity.id
        conversation.lastActivityAt = activity.createdAt ?? Date.now()
        conversation.save()
      })

      try {
        await Promise.all(activity.media.map((m) => this.service.media.upload(m)))
      } catch (error) {
        // Surface a clear message for a too-large attachment when the 413 is
        // readable. Oversized files are normally caught client-side before
        // upload, so any other failure here is a genuine upload/network error
        // and gets a neutral message rather than a misleading size hint.
        const isTooLarge = error instanceof PayloadTooLargeError
        throw new Error(
          isTooLarge
            ? 'This attachment is too large to send. Please try a smaller file.'
            : "We couldn't upload your attachment. Please check your connection and try again.",
          { cause: error },
        )
      }

      const { conversation: conversationJson, activity: activityJson } =
        await this.service.transport.communication.activities.send({
          body: activity.body,
          media: activity.media,
          id: activity.id,
          conversationId: conversation.id,
          ...address,
          sendAndMarkDone: activity.options?.markAsDone,
        })

      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- UXP-3744 - Fix Promise-related ESLint issues
      this.service.conversation.collection.load(conversationJson)

      if (activity.type === 'message' && !conversation.directNumberId) {
        this.service.analytics.inbox.messageSent()
      }

      const activityCollection = await this.collection.load(activityJson)
      return activityCollection[0]
    } catch (error) {
      runInAction(() => {
        activity.status = 'failed'
        activity.error = {
          code: 400,
          message: 'Failed to send',
        }
        if (
          typeof error === 'object' &&
          error &&
          'message' in error &&
          typeof error.message === 'string'
        ) {
          activity.error.secondaryMessage = error.message
        }
        this.collection.put(activity)

        throw error
      })
    }
  }

  delete = (activity: ActivityModel) => {
    this.collection.delete(activity)
  }

  private async addReaction(reaction: ActivityReaction) {
    try {
      reaction.activity.reactions.push(reaction)
      this.collection.put(reaction.activity)
      return await this.service.transport.communication.reactions.post(reaction.toJSON())
    } catch (error) {
      reaction.activity.deleteReactionLocally(reaction)
      throw error
    }
  }

  private async deleteReaction(reaction: ActivityReaction) {
    try {
      reaction.activity.deleteReactionLocally(reaction)
      this.collection.put(reaction.activity)
      return await this.service.transport.communication.reactions.delete(reaction.id)
    } catch (error) {
      reaction.activity.reactions.push(reaction)
      throw error
    }
  }

  getReaction(emoji: string, reactions: ActivityReaction[]) {
    if (!this.service.user.current) {
      return undefined
    }

    return this.service.emoji.getReaction(
      emoji,
      this.service.emoji,
      reactions,
      this.service.user.current.id,
    )
  }

  async toggleReaction(activity: ActivityModel, emoji: string) {
    if (!this.service.user.current) {
      return
    }

    const reaction = this.getReaction(emoji, activity.reactions)
    if (reaction) {
      return await this.deleteReaction(reaction)
    } else {
      const body = this.service.emoji.getEmojiWithSkinTone(emoji)
      return await this.addReaction(
        new ActivityReaction(activity, {
          body,
          userId: this.service.user.current.id,
        }),
      )
    }
  }

  resolve(activity: ActivityModel) {
    if (!this.service.user.current) {
      return
    }
    activity.resolvedAt = Date.now()
    activity.resolvedBy = this.service.user.current.id
    return this.service.transport.communication.activities.resolve(activity.id)
  }

  unresolve(activity: ActivityModel) {
    activity.resolvedAt = null
    activity.resolvedBy = null
    return this.service.transport.communication.activities.unresolve(activity.id)
  }

  async deleteRecordings(activity: ActivityModel, mediaIds: string[]) {
    await this.service.voice.recordings.bulkDelete(activity.id, mediaIds)
    activity.media = activity.media.filter((item) => !mediaIds.includes(item.id))
  }

  private getActivityIdsByConversationIds(conversationIds: string[]) {
    return this.service.storage
      .table<ActivityRepository>('activity')
      .findIdsByConversationIds(conversationIds)
  }

  async deleteByConversationIds(conversationIds: string[]) {
    const activityIdsToRemove =
      await this.getActivityIdsByConversationIds(conversationIds)

    this.collection.deleteBulk(activityIdsToRemove)

    return activityIdsToRemove
  }

  /**
   * Fetches the most recently updated activities for a given conversation from
   * the backend.
   *
   * Makes use of the `lastFetchedAt` property to only fetch activities with changes
   * since the last time we fetched activities for this conversation.
   *
   * @param conversation
   * @returns
   */
  fetchRecentChanges = async (conversation: Conversation) => {
    const lastFetchedAt = this.lastFetchedAt[conversation.id]
    const since = lastFetchedAt ? new Date(lastFetchedAt) : undefined

    if (conversation.isNew) {
      return []
    }

    const { result } = await this.service.transport.communication.activities.list({
      id: conversation.id,
      since,
    })

    const activities = await this.collection.load(result)

    runInAction(() => {
      this.lastFetchedAt[conversation.id] = Math.max(
        lastFetchedAt || 0,
        ...activities.map((c) => c.updatedAt ?? 0),
      )
    })
  }

  fetchCallActivities = async (
    params: PostCallActivitiesRequest,
    transactionConfiguration?: TransactionConfiguration,
  ) => {
    const response = await this.service.transport.communication.activities.calls.list(
      params,
      transactionConfiguration,
    )

    const { calls, voicemails, pageInfo } = response ?? {
      calls: [],
      voicemails: [],
      pageInfo: {
        startId: null,
        endId: null,
        prevId: null,
        nextId: null,
      },
    }

    const [callActivities] = await Promise.all([
      calls ? this.collection.load(calls) : Promise.resolve([]),
      voicemails ? this.collection.load(voicemails) : Promise.resolve([]),
    ])

    return { activities: callActivities, pageInfo }
  }

  private handleWebsocket() {
    this.service.transport.onNotificationData.subscribe((msg) => {
      switch (msg.type) {
        case 'reaction-update':
          if (isActivityReaction(msg.reaction)) {
            return this.handleReactionUpdate(msg.reaction)
          }
          break
        case 'reaction-delete':
          if (isActivityReaction(msg.reaction)) {
            return this.handleReactionDelete(msg.reaction)
          }
          break

        case 'activity-label-update':
          this.handleActivityLabelsUpdate(msg)
          break
      }
    })
  }

  private handleActivityLabelsUpdate(value: ActivityLabelsUpdate) {
    const activity = this.collection.get(value.activityId)
    if (!activity) {
      return
    }

    activity.tags = value.labels
    this.collection.put(activity)
  }

  private handleReactionUpdate(value: ActivityDecodableReaction) {
    const activity = this.collection.get(value.activityId)
    if (!activity) {
      return
    }
    const object = value.commentId
      ? activity.comments.find((comment) => comment.id === value.commentId)
      : activity
    if (!object) {
      return
    }
    const reaction = object.reactions.find((reaction) => reaction.id == value.id)
    if (reaction) {
      reaction.deserialize(value)
    } else {
      object.reactions.push(new ActivityReaction(object, value))
    }
  }

  private handleReactionDelete(value: ActivityDecodableReaction) {
    const activity = this.collection.get(value.activityId)
    if (!activity) {
      return
    }
    const reaction = activity.reactions.find((reaction) => reaction.id == value.id)

    if (reaction) {
      return activity.deleteReactionLocally(reaction)
    }

    // TODO: Get the backend to return the commentId associated with a deleted reaction on a comment via websocket
    // Checks comments (instead of activities) for a reaction to delete
    activity.comments.forEach((comment) => {
      const commentReactionToRemove = comment.reactions.findIndex(
        (reaction) => reaction.id === value.id,
      )
      if (commentReactionToRemove >= 0) {
        comment.reactions.splice(commentReactionToRemove, 1)
      }
    })
  }
}

function addressForConversation(conversation: Conversation): SendActivityAddress | null {
  const { phoneNumber: to, phoneNumberId, directNumberId } = conversation

  if (!to) {
    return null
  }

  if (phoneNumberId) {
    return { to, phoneNumberId, directNumberId }
  }

  if (directNumberId) {
    return { to, directNumberId }
  }

  return null
}
